import { makeAbsoluteUrl } from "@/utils/makeAbsoluteUrl"

export class AthleteAffiliation {
  constructor(teamAffiliations, clubAffiliations, highSchoolAffiliations) {
    this.clubAffiliations = clubAffiliations;
    this.teamAffiliations = teamAffiliations;
    this.highSchoolAffiliations = highSchoolAffiliations;

    this.primaryClubAffiliation = null;
  }

  getAffiliation() {
    const affiliation = [
      this.getClubTeamAffiliation(),
      this.getHighSchoolTeamAffiliation(),
      this.getIntegratedClubAffiliation(),
      this.getIntegratedHighSchoolAffiliation(),
      this.getNonIntegratedClubAffiliation(),
      this.getNonIntegratedHighSchoolAffiliation(),
    ].find(element => !!element)

    return affiliation ?? false;
  }

  getClubTeamAffiliation() {
    const primaryClubAffiliation = this.getPrimaryClubAffiliation();
    const teamAffiliations = this.teamAffiliations.filter(team => team.team?.organization?.kind === 'club').slice().sort((a, b) => a.team?.organization?.id - b.team?.organization?.id);

    const clubTeam = primaryClubAffiliation ? teamAffiliations.find(team => team.team?.organization?.id === primaryClubAffiliation?.organization?.id) : teamAffiliations.shift();

    if (clubTeam) {
      return this.createAffiliationObject(this.formatTeamAffiliationValue(clubTeam), 'club');
    }

    return false;
  }

  getHighSchoolTeamAffiliation() {
    const highSchoolTeam = this.teamAffiliations.find(team => team.team?.organization?.kind === 'highSchool');
    if (highSchoolTeam) {
      return this.createAffiliationObject(this.formatTeamAffiliationValue(highSchoolTeam), 'highschool');
    }
  }

  getIntegratedClubAffiliation() {
    const integratedClub = this.getPrimaryClubAffiliation();
    
    if (integratedClub) {
      return this.createAffiliationObject(this.formatIntegratedOrganizationValue(integratedClub), 'club');
    }
  }

  getNonIntegratedClubAffiliation() {
    const nonIntegratedClub = this.clubAffiliations.find(aff => !aff.isIntegrated);
    if (nonIntegratedClub) {
      return this.createAffiliationObject(this.formatOrganizationName(nonIntegratedClub), 'club');
    }
  }

  getIntegratedHighSchoolAffiliation() {
    const integratedHighSchool = this.highSchoolAffiliations.find(aff => aff.isIntegrated);
    if (integratedHighSchool) {
      return this.createAffiliationObject(this.formatIntegratedOrganizationValue(integratedHighSchool), 'highschool');
    }
  }

  getNonIntegratedHighSchoolAffiliation() {
    const nonIntegratedHighSchool = this.highSchoolAffiliations.find(aff => !aff.isIntegrated);
    if (nonIntegratedHighSchool && nonIntegratedHighSchool.organization?.name !== null) {
      return this.createAffiliationObject(this.formatOrganizationName(nonIntegratedHighSchool), 'highschool');
    }
  }

  getPrimaryClubAffiliation() {
    if (this.clubAffiliations.length === 0) {
      return null;
    }

    if (!this.primaryClubAffiliation) {
      this.primaryClubAffiliation = this.clubAffiliations
        .slice()
        .filter(aff => aff.isIntegrated)
        .sort((a, b) => {
          if (a.order === b.order) {
            return a.organization.id - b.organization.id;
          }
          return b.order - a.order;
        })
        .shift();
    }

    return this.primaryClubAffiliation;
  }

  createAffiliationObject(value, type) {
    return { value, type };
  }

  formatTeamAffiliationValue(team) {
    const affiliationName = this.formatTeamAffiliationName(team);
    const absoluteUrl = this.formatTeamUrl(team);
    return `<a href="${absoluteUrl}">${affiliationName}</a>`;
  }

  formatTeamAffiliationName(team) {
    const teamName = team.team.name ? `${team.team.name}, ` : '';
    const orgName = team.team.organization?.name ? team.team.organization.name : '';

    return teamName + orgName;
  }

  formatTeamUrl(team) {
    return makeAbsoluteUrl(`/organization/${team.team.organization.urlSlug}/${team.team.urlSlug}`);
  }

  formatIntegratedOrganizationValue(organization) {
    const name = this.formatOrganizationName(organization);
    const absoluteUrl = this.formatOrganizationUrl(organization);
    return `<a href="${absoluteUrl}">${name}</a>`;
  }

  formatOrganizationName(organization) {
    const teamName = organization.team?.name ? `${organization.team.name}` : '';
    const orgName = organization.organization?.name ? organization.organization.name : '';
    const separator = teamName && orgName ? ', ' : '';
    return teamName + separator + orgName;
  }

  formatOrganizationUrl(organization) {
    return makeAbsoluteUrl(`/organization/${organization.organization.urlSlug}`);
  }
}